import React, { ReactNode } from 'react';

import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import omitProps from '@hocs/omit-props';
import isObject from 'lodash/isObject';
import toUpper from 'lodash/toUpper';
import styled from 'styled-components';
import classnames from 'classnames';

import { isInSelectionMode, ProductIdOrOptions } from '../../store/productSelection/actions';
import CatalogueListItem from '../../shop/catalogue/CatalogueListItem';
import { CatalogueProductTile } from '../../shop/catalogue/CatalogueProductTile';
import $ from '../utils/query';
import historyUtils from '../utils/utils-routing';
import authorizationUtils from '../utils/utils-authorization';
import { concreteLoanFormatNameToKey, isLoanFormatKey, isLoanFormatObject } from '../../store/loanFormat/actions';
import { LoanFormatState } from '../../store/loanFormat/state';
import { AppState } from '../../store/appState';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { isListProduct, Product, ProductMetaData, UsageCondition } from '../../../models/Product';
import { OrderListsState, OrderListState } from '../../store/orderlist/state';
import { ProductSelectionState } from '../../store/productSelection/state';
import { DISPLAY_TYPE_LIST, DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../store/displayType/types';
import { GroupItem } from '../../../models/GroupItem';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { LibraryManagedProductGroupOnSiteOut } from '../../store/api-catman';
import i18n from '../../../i18n';
import { SingleAddOptions, SingleCopyOrMoveOptions } from '../../../models/ListContentCheckOptions';

const TileContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

export type ProductListItemProps = {
    readonly product: ProductMetaData & Article;
    // FIXME: the usage condition should be part of the `Article.price`
    readonly usageCondition?: UsageCondition;
    // FIXME: there are multiple loan format representations like 'EBOOKS', 'eBooks', ...
    //  and all could be passed in here, so we have to type it as `string` for now
    readonly loanFormat?: string | null;
    readonly minimalView?: boolean;
    readonly isInPartnerContext?: boolean;
    readonly onProductImageClick: () => void;
    // props which are passed through
    readonly orderlist: OrderListState;
    readonly orderlists: OrderListsState['list'];
    readonly activeList?: any; // FIXME: is this even used?
    readonly productSelection: ProductSelectionState | null;
    readonly searchTerms: ReadonlyArray<string> | null;
    readonly productGroups: LibraryManagedProductGroupOnSiteOut[];
    // callbacks which are passed through
    readonly onAddPrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    readonly onAddToGroup?: (productGroupId: number, productId: string) => void;
    readonly onPageChange?: (page: number, reload?: boolean) => void;
    readonly onSelectProduct: () => void;
    readonly onRemoveProductFromCart?: (product: ProductMetaData & Article) => void;
    readonly onRemoveProductFromList?: (product: ProductMetaData & Article) => void | ((productId: string) => void);
    readonly onUpdateUsageCondition?: (
        product: ProductMetaData & Article,
        newUsageCondition: UsageCondition,
        newSubscriptionPeriodType?: SubscriptionPeriodType
    ) => Promise<void>;
    // todo do we need this?
    readonly onCreateNewListClicked?: (newSelectionListName: string) => void;
    readonly onCopyAmountChange?: (
        productOrId: string | (ProductMetaData & Article),
        amount: number,
        command?: string
    ) => void;
    readonly className?: string;
    readonly actionButtons?: ReactNode;
    readonly showSelectionLists?: boolean;
    readonly hoverListener?: (hovered: boolean) => void;
    readonly isDeleteAction?: boolean;
    readonly currentListId?: string;
};

type OwnProps = {
    readonly products: ReadonlyArray<Product>;
    readonly currentPage: number;
    readonly className?: string;
    readonly forceStoreLoanFormat?: boolean;
    readonly displayType: typeof DISPLAY_TYPE_TILE | typeof DISPLAY_TYPE_LIST | typeof DISPLAY_TYPE_MINIMAL;
    readonly isProductSelectable?: boolean;
    readonly listItemComponent: React.JSXElementConstructor<ProductListItemProps>;
    readonly tileItemComponent: React.JSXElementConstructor<ProductListItemProps>;
    // props which are passed through
    readonly orderlist: OrderListState;
    readonly orderlists: OrderListsState['list'];
    readonly activeList?: any;
    readonly productSelection: ProductSelectionState;
    readonly searchTerms?: ReadonlyArray<string>;
    readonly productGroups: LibraryManagedProductGroupOnSiteOut[];
    // callbacks which are passed through
    readonly onAddPrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    readonly onAddToGroup?: (productGroupId: number, productId: string) => void;
    readonly onPageChange?: (page: number, reload?: boolean) => void;
    readonly onSelectProduct?: (product: ProductIdOrOptions) => void;
    readonly onRemoveProductFromCart?: (product: ProductMetaData & Article) => void;
    readonly onRemoveProductFromList?: (product: ProductMetaData & Article) => void | ((productId: string) => void);
    readonly onUpdateUsageCondition?: (
        product: ProductMetaData & Article,
        newUsageCondition: UsageCondition
    ) => Promise<void>;
    // todo do we need this?
    readonly onCreateNewListClicked?: (newSelectionListName: string) => void;
    readonly onCopyAmountChange?: (
        productOrId: string | (ProductMetaData & Article),
        amount: number,
        command?: string
    ) => void;

    readonly onRemoveGroupedItemFromList?: (item: GroupItem) => void;
    readonly loading?: boolean;
    readonly currentListId?: string;
};

type Props = {
    readonly grouping?: any;
    readonly loanFormat?: LoanFormatState | string;
    readonly isInPartnerContext?: boolean;
};

type State = {
    readonly pageChanged: boolean;
};

class ProductList extends React.Component<Props & OwnProps, State> {
    constructor(props) {
        super(props);
        this.onProductImageClick = this.onProductImageClick.bind(this);
        this.state = { pageChanged: false };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps, this.props);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.currentPage !== this.props.currentPage) {
            this.setState({ pageChanged: true });
        }

        if (this.props.products.length > 0 && this.state.pageChanged) {
            if (this.props.grouping.id) {
                const itemDiv = $('.item-products')[0].parentElement;
                $.scrollTo(itemDiv, true);
                $.scrollDown(60);
            } else {
                $.scrollTo('.pagination');
            }
            this.setState({ pageChanged: false });
        }
    }

    onProductImageClick(product: ProductIdOrOptions) {
        if (this.props.isProductSelectable && isInSelectionMode(this.props.productSelection)) {
            this.props.onSelectProduct?.(product);
        } else {
            historyUtils.pushProductId(typeof product === 'string' ? product : product.id);
        }
    }

    render() {
        const firstOrderlistEntry = this.props.orderlists?.[0];
        const activeList = this.props.activeList || firstOrderlistEntry || {};
        const itemComponent =
            this.props.displayType !== DISPLAY_TYPE_LIST ? this.props.tileItemComponent : this.props.listItemComponent;
        const isMinimal = this.props.displayType === DISPLAY_TYPE_MINIMAL;
        let loanFormat: string | null = null;
        const forceStoreLoanFormat = this.props.forceStoreLoanFormat;

        if (forceStoreLoanFormat && this.props.loanFormat) {
            if (isObject(this.props.loanFormat) && isLoanFormatObject(this.props.loanFormat)) {
                loanFormat = this.props.loanFormat.key;
            } else if (isLoanFormatKey(this.props.loanFormat)) {
                loanFormat = toUpper(this.props.loanFormat) ?? 'ALL';
            } else {
                throw new Error(i18n.t('notifications.productList.error', { loanFormat: this.props.loanFormat }));
            }
        }

        const products = this.props.products.flatMap(product => {
            const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
            const loanFormatKey = forceStoreLoanFormat ? mapLoanFormat(loanFormat) : productLoanFormatKey;
            // Merge listitem to product.price
            if (isListProduct(product)) {
                return product.lineItems.map(lineItem => {
                    const price = lineItem.price ?? {
                        lendingModel: undefined,
                        usageLimit: undefined,
                        termLimit: undefined
                    };

                    const usageCondition = lineItem.price
                        ? {
                              lendingModel: lineItem.price.lendingModel!,
                              usageLimit: lineItem.price.usageLimit!,
                              termLimit: lineItem.price.termLimit!
                          }
                        : {};

                    const lineItemKey = {
                        productId: product.id,
                        usageCondition,
                        subscriptionPeriodType: lineItem?.subscriptionPeriod?.type
                    };

                    const productOptions = {
                        id: product.id,
                        price,
                        subscriptionPeriodType: lineItem?.subscriptionPeriod?.type
                    };

                    return React.createElement(itemComponent, {
                        ...this.props,
                        onAddToGroup: this.props.onAddToGroup,
                        product: {
                            ...product,
                            price,
                            availablePrices: lineItem.availablePrices,
                            subscriptionPeriod: lineItem.subscriptionPeriod,
                            subscriptionPeriodType: lineItem.subscriptionPeriod?.type,
                            // FIXME: copyAmount should be part of the `price` property
                            ...({ copyAmount: lineItem.price?.copyAmount } as any),
                            lineItemKey
                        },
                        usageCondition,
                        loanFormat: loanFormatKey,

                        // defining a print order via the replacer param in the stringify method
                        // keeps the unique identifier key idempotent for each item
                        key: JSON.stringify(lineItemKey, [
                            'productId',
                            'usageCondition',
                            'lendingModel',
                            'usageLimit',
                            'termLimit',
                            'subscriptionPeriodType'
                        ]),
                        productSelection: this.props.isProductSelectable ? this.props.productSelection : null,
                        searchTerms: this.props.searchTerms ?? null,
                        activeList,
                        onProductImageClick: () => this.onProductImageClick(productOptions),
                        onSelectProduct: () => this.props.onSelectProduct?.(productOptions),
                        minimalView: isMinimal,
                        isInPartnerContext: this.props.isInPartnerContext
                    });
                });
            } else {
                const price = product.price;
                const usageCondition = price
                    ? {
                          lendingModel: price.lendingModel!,
                          usageLimit: price.usageLimit!,
                          termLimit: price.termLimit!
                      }
                    : undefined;
                const productOptions = { id: product.id };

                return React.createElement(itemComponent, {
                    ...this.props,
                    onAddToGroup: this.props.onAddToGroup,
                    product,
                    loanFormat: loanFormatKey,
                    key: `${product.id}-${product.price?.lendingModel}-${product.price?.usageLimit}-${product.price?.termLimit}`,
                    productSelection: this.props.isProductSelectable ? this.props.productSelection : null,
                    searchTerms: this.props.searchTerms ?? null,
                    activeList,
                    onProductImageClick: () => this.onProductImageClick(productOptions),
                    onSelectProduct: () => this.props.onSelectProduct?.(productOptions),
                    minimalView: isMinimal,
                    isInPartnerContext: this.props.isInPartnerContext,
                    usageCondition
                });
            }
        });
        const isListView = this.props.displayType === DISPLAY_TYPE_LIST;
        const ContainerComponent = isListView ? 'div' : TileContainer;
        const className = classnames(this.props.className, 'overwrite-legacy-css-for-ProductList', {
            'product-tile-container-new': !isListView,
            'product-list-container-new': isListView,
            'min-h-screen': this.props.loading
        });

        return (
            <ContainerComponent className={className} data-displayname="ProductList" data-testid="ProductList">
                {products}
            </ContainerComponent>
        );
    }
}

(ProductList as any).defaultProps = {
    forceStoreLoanFormat: false,
    products: [],
    isProductSelectable: true,
    productSelection: { selected: [], excluded: [] },
    activeList: null,
    searchTerms: [],
    listItemComponent: CatalogueListItem,
    tileItemComponent: CatalogueProductTile,
    onAddToList: () => {},
    onPageChange: () => {},
    onSelectProduct: () => {}
};

function mapStateToProps(state: AppState): Props {
    return {
        grouping: state.grouping,
        loanFormat: state.loanFormat.key,
        isInPartnerContext: authorizationUtils.isInPartnerContext(state.authentication)
    };
}

export default compose(
    connect(mapStateToProps),
    omitProps(
        'router',
        'match',
        'location',
        'history',
        'aggregations',
        'historyRestoration',
        'authentication',
        'analysis',
        'isSticky'
    )
)(ProductList);
